import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, auth, fileUrl } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, MapPin, HeartPulse, Languages, Play, Loader2, CheckCircle2, Lock, Clock, RotateCw, BookOpen } from "lucide-react";

export default function QuizEntry() {
  const { token } = useParams();
  const nav = useNavigate();
  const { branding } = useBranding();
  const brandColor = branding?.theme_color || "#0EA5E9";

  // If the caller is already a logged-in employee, we skip the identity form
  // and use the session for verification (backend resolves emp from token).
  const sessionUser = auth.user();
  const isLoggedInEmp = !!(auth.token() && sessionUser?.emp_db_id);

  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [empId, setEmpId] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [phase, setPhase] = useState("");
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Sequential flow state
  const [flowState, setFlowState] = useState(null); // result of /assessment-state
  const [resetPhase, setResetPhase] = useState(""); // "pre"|"post" — for the dialog
  const [resetReason, setResetReason] = useState("");

  useEffect(() => {
    api.get(`/public/q/${token}`).then(r => setInfo(r.data)).catch(e => setErr(e?.response?.data?.detail || "Quiz not found"));
    // Prefill from logged-in session first (highest priority — bypasses re-verification).
    if (isLoggedInEmp) {
      setEmpId(sessionUser.emp_id || "");
      setEmail(sessionUser.email || "");
    } else {
      try {
        const pre = JSON.parse(sessionStorage.getItem("emp_prefill") || "null");
        if (pre) { setEmpId(pre.emp_id); setEmail(pre.email); }
      } catch { /* ignore */ }
    }
    // If user has just completed a step in this assessment flow, restore creds + auto-load ladder
    try {
      const flowCreds = JSON.parse(sessionStorage.getItem(`flow-creds-${token}`) || "null");
      if (flowCreds?.emp_id) {
        setEmpId(flowCreds.emp_id);
        setEmail(flowCreds.email || "");
        setDob(flowCreds.dob || "");
      }
    } catch { /* ignore */ }
  }, [token, isLoggedInEmp, sessionUser?.emp_id, sessionUser?.email]);

  // Auto-load flow state as soon as we have the quiz info + (session OR restored creds).
  useEffect(() => {
    if (!info || !info.assessment_flow_enabled || flowState) return;
    if (info.enforce_geofence && !coords) return; // need location first
    if (isLoggedInEmp) {
      loadFlowState();
      return;
    }
    try {
      const c = JSON.parse(sessionStorage.getItem(`flow-creds-${token}`) || "null");
      if (c?.emp_id && empId === c.emp_id) {
        // creds restored from storage → load state automatically
        loadFlowState();
      }
    } catch { /* ignore */ }
  }, [info, coords, isLoggedInEmp]);

  function captureLocation() {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); setLocating(false); toast.success("Location captured"); },
      () => { setLocating(false); toast.error("Allow location access to continue"); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }

  const fields = info?.validation_fields || [];
  const needsEmail = fields.includes("email");
  const needsDob = fields.includes("dob");
  const flowEnabled = !!info?.assessment_flow_enabled;

  function validateIdentity() {
    if (isLoggedInEmp) {
      if (info?.enforce_geofence && !coords) { toast.error("Please capture your location first"); return false; }
      return true; // session handles identity
    }
    if (!empId) { toast.error("Enter your Employee ID"); return false; }
    if (needsEmail && !email) { toast.error("Enter your registered email"); return false; }
    if (needsDob && !dob) { toast.error("Enter your Date of Birth"); return false; }
    if (info?.enforce_geofence && !coords) { toast.error("Please capture your location first"); return false; }
    return true;
  }

  async function loadFlowState() {
    if (!validateIdentity()) return;
    setSubmitting(true);
    try {
      const r = await api.post("/public/q/assessment-state", {
        token, emp_id: empId || sessionUser?.emp_id || "",
        email: needsEmail ? email : "", dob: needsDob ? dob : "",
      });
      // Persist creds so subsequent navigations (Training Viewer, refresh) work.
      const persistEmp = empId || r.data?.employee?.emp_id || sessionUser?.emp_id || "";
      sessionStorage.setItem(`flow-creds-${token}`, JSON.stringify({ emp_id: persistEmp, email, dob }));
      // Auto-redirect: when Pre-Test is done but Training isn't, push the user straight into Training
      // (Training is the mandatory next step before any further attempts.)
      const s = r.data;
      if (s.enabled && s.pre.completed && s.training.status !== "completed" && s.training.asset) {
        toast.info("Pre-Test complete — continuing your Training…");
        nav(`/training/${token}`);
        return;
      }
      setFlowState(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setSubmitting(false); }
  }

  async function startPhase(targetPhase) {
    setSubmitting(true);
    try {
      const r = await api.post("/public/q/verify", {
        token, emp_id: empId || sessionUser?.emp_id || "",
        email: needsEmail ? email : "", dob: needsDob ? dob : "",
        phase: targetPhase, lat: coords?.lat, lng: coords?.lng,
      });
      sessionStorage.setItem(`attempt-${r.data.attempt_id}`, JSON.stringify(r.data));
      nav(`/play/${r.data.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start");
    } finally { setSubmitting(false); }
  }

  async function start() {
    if (!validateIdentity()) return;
    if (flowEnabled) { await loadFlowState(); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/public/q/verify", {
        token, emp_id: empId || sessionUser?.emp_id || "",
        email: needsEmail ? email : "", dob: needsDob ? dob : "",
        phase: info?.lock_phase ? info?.default_phase : phase,
        lat: coords?.lat, lng: coords?.lng,
      });
      sessionStorage.setItem(`attempt-${r.data.attempt_id}`, JSON.stringify(r.data));
      nav(`/play/${r.data.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setSubmitting(false); }
  }

  async function submitResetRequest() {
    if (!resetPhase) return;
    try {
      const r = await api.post("/public/q/reset-request", {
        token, emp_id: empId || sessionUser?.emp_id || "",
        email: needsEmail ? email : "", dob: needsDob ? dob : "",
        phase: resetPhase, reason: resetReason || "",
      });
      if (r.data?.already_pending) {
        toast.info("A reset is already pending for this step.");
      } else {
        toast.success("Reset requested. An admin will review it shortly.");
      }
      setResetPhase("");
      setResetReason("");
      await loadFlowState(); // refresh state
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit request");
    }
  }

  if (err) return <Center><div className="text-center p-10"><h1 className="text-2xl font-bold">Oops</h1><p className="text-slate-500 mt-2">{err}</p></div></Center>;
  if (!info) return <Center><Loader2 className="h-6 w-6 animate-spin text-sky-500"/></Center>;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${brandColor}10, #fff)` }}>
      <div className="max-w-md mx-auto p-6 space-y-6 py-10">
        <div className="text-center pop-in">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl text-white shadow-lg" style={{ background: brandColor, boxShadow: `0 15px 35px -10px ${brandColor}` }}>
            {branding?.logo_path ? (
              <img src={fileUrl(branding.logo_path)} alt="" className="h-14 w-14 rounded-2xl object-cover"/>
            ) : (
              <HeartPulse className="h-7 w-7"/>
            )}
          </div>
          <div className="text-xs uppercase tracking-[0.22em] font-bold mt-3" style={{ color: brandColor }}>{branding?.hospital_name || "MedLearn"} · Training</div>
          <h1 className="font-display font-black text-3xl mt-1 text-slate-900">{info.title}</h1>
        </div>

        {info.cover_image && (
          <div className="rounded-3xl overflow-hidden aspect-[16/10] border border-slate-200 shadow-sm">
            <img src={info.cover_image.startsWith("http") ? info.cover_image : fileUrl(info.cover_image)} alt="" className="w-full h-full object-contain bg-slate-100"/>
          </div>
        )}

        <div className="bg-white rounded-3xl border border-slate-200 p-5 space-y-2">
          {info.description_en && <p className="text-slate-700">{info.description_en}</p>}
          {info.description_hi && <p className="text-slate-500 leading-relaxed">{info.description_hi}</p>}
          <div className="flex gap-2 flex-wrap pt-2">
            <Pill icon={Languages} text="English + हिन्दी"/>
            <Pill icon={ShieldCheck} text={`${info.num_questions} questions`}/>
            <Pill icon={MapPin} text={info.enforce_geofence ? "Location locked" : "Open access"}/>
            {flowEnabled && <Pill icon={BookOpen} text="Pre → Train → Post"/>}
          </div>
        </div>

        {!flowState && !isLoggedInEmp && (
          <IdentityCard
            info={info} needsEmail={needsEmail} needsDob={needsDob}
            empId={empId} setEmpId={setEmpId}
            email={email} setEmail={setEmail}
            dob={dob} setDob={setDob}
            phase={phase} setPhase={setPhase}
            coords={coords} captureLocation={captureLocation} locating={locating}
            submitting={submitting} start={start} brandColor={brandColor}
            flowEnabled={flowEnabled}
          />
        )}

        {/* Logged-in employee: show a compact identity confirmation + start card (no re-verification). */}
        {!flowState && isLoggedInEmp && (
          <LoggedInStartCard
            info={info}
            sessionUser={sessionUser}
            phase={phase} setPhase={setPhase}
            coords={coords} captureLocation={captureLocation} locating={locating}
            submitting={submitting} start={start} brandColor={brandColor}
            flowEnabled={flowEnabled}
          />
        )}

        {flowState && (
          <AssessmentLadder
            flowState={flowState}
            onStart={startPhase}
            onTraining={() => {
              const persistEmp = empId || flowState?.employee?.emp_id || sessionUser?.emp_id || "";
              sessionStorage.setItem(`flow-creds-${token}`, JSON.stringify({ emp_id: persistEmp, email, dob }));
              nav(`/training/${token}`);
            }}
            onResetClick={(p) => setResetPhase(p)}
            onRefresh={loadFlowState}
            submitting={submitting}
            brandColor={brandColor}
          />
        )}

        <Dialog open={!!resetPhase} onOpenChange={(v) => !v && setResetPhase("")}>
          <DialogContent data-testid="reset-dialog">
            <DialogHeader>
              <DialogTitle>Request a reset — {resetPhase === "pre" ? "Pre-Test" : "Post-Test"}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500">You&apos;ve used all your allowed attempts. An admin will review your request.</p>
            <Textarea placeholder="Reason (optional)" value={resetReason} onChange={(e) => setResetReason(e.target.value)} data-testid="reset-reason"/>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPhase("")}>Cancel</Button>
              <Button onClick={submitResetRequest} style={{ background: brandColor, color: "white" }} data-testid="reset-submit-btn">Submit Request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function LoggedInStartCard({ info, sessionUser, phase, setPhase, coords, captureLocation, locating, submitting, start, brandColor, flowEnabled }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-5 space-y-4" data-testid="logged-in-start-card">
      <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3">
        <div className="h-9 w-9 rounded-xl bg-emerald-500 text-white grid place-items-center flex-shrink-0">
          <CheckCircle2 className="h-4 w-4"/>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Signed in</div>
          <div className="font-bold text-slate-900 truncate">{sessionUser?.name || "—"}</div>
          <div className="text-[11px] text-slate-500 truncate">{sessionUser?.emp_id} · {sessionUser?.email}</div>
        </div>
      </div>

      {!flowEnabled && (
        <div className="space-y-2" data-testid="phase-picker">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Submission Type</Label>
          {info?.lock_phase ? (
            <div className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm">
              <span className="font-bold capitalize">{(info?.default_phase || "—")}-Session</span>
              <span className="text-slate-500 ml-2">(set by admin)</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "", label: "—" },
                { v: "pre", label: "Pre-Session" },
                { v: "post", label: "Post-Session" },
              ].map((opt) => (
                <button key={opt.v} type="button" onClick={()=>setPhase(opt.v)}
                  data-testid={`phase-${opt.v || "none"}`}
                  className={`h-12 rounded-xl border-2 font-bold text-sm transition ${phase === opt.v ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 hover:bg-slate-50 text-slate-600"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {info.enforce_geofence && (
        <div className="rounded-2xl bg-sky-50 border border-sky-200 p-3 flex items-center gap-3">
          <MapPin className="h-5 w-5 text-sky-600 flex-shrink-0"/>
          <div className="flex-1 text-sm">
            {coords ? (
              <div className="text-sky-700 font-bold">Location captured ✓</div>
            ) : (
              <div className="text-sky-700">Location required for this training</div>
            )}
            <div className="text-xs text-sky-700/70">Allowed: {info.locations.map(l=>l.name).join(", ") || "—"}</div>
          </div>
          <Button variant="outline" size="sm" onClick={captureLocation} disabled={locating} data-testid="quiz-capture-location">
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : (coords ? "Refresh" : "Allow")}
          </Button>
        </div>
      )}

      <Button onClick={start} disabled={submitting}
        data-testid="quiz-start-btn"
        className="w-full h-14 rounded-2xl text-white font-bold text-lg shadow-lg"
        style={{ background: brandColor, boxShadow: `0 15px 35px -10px ${brandColor}` }}>
        {submitting ? <Loader2 className="h-5 w-5 animate-spin"/> : <><Play className="h-5 w-5 mr-2"/>{flowEnabled ? "Continue" : "Start Quiz"}</>}
      </Button>
    </div>
  );
}

function IdentityCard({ info, needsEmail, needsDob, empId, setEmpId, email, setEmail, dob, setDob, phase, setPhase, coords, captureLocation, locating, submitting, start, brandColor, flowEnabled }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-5 space-y-4">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Verify Yourself</div>
        <p className="text-sm text-slate-500">Only registered employees can take this training.</p>
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wider text-slate-500">Employee ID</Label>
        <Input data-testid="quiz-empid-input" placeholder="e.g. EMP001" value={empId} onChange={e=>setEmpId(e.target.value.trim())} className="rounded-xl h-12 text-lg"/>
      </div>
      {needsEmail && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Registered Email</Label>
          <Input data-testid="quiz-email-input" type="email" placeholder="you@hospital.com" value={email} onChange={e=>setEmail(e.target.value.trim())} className="rounded-xl h-12 text-lg"/>
        </div>
      )}
      {needsDob && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Date of Birth</Label>
          <Input data-testid="quiz-dob-input" type="date" value={dob} onChange={e=>setDob(e.target.value)} className="rounded-xl h-12 text-lg"/>
          <div className="text-[11px] text-slate-400">Pick from calendar — must match what&apos;s on file.</div>
        </div>
      )}

      {!flowEnabled && (
        <div className="space-y-2" data-testid="phase-picker">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Submission Type</Label>
          {info?.lock_phase ? (
            <div className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm">
              <span className="font-bold capitalize">{(info?.default_phase || "—")}-Session</span>
              <span className="text-slate-500 ml-2">(set by admin)</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "", label: "—" },
                { v: "pre", label: "Pre-Session" },
                { v: "post", label: "Post-Session" },
              ].map((opt) => (
                <button key={opt.v} type="button" onClick={()=>setPhase(opt.v)}
                  data-testid={`phase-${opt.v || "none"}`}
                  className={`h-12 rounded-xl border-2 font-bold text-sm transition ${phase === opt.v ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 hover:bg-slate-50 text-slate-600"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {info.enforce_geofence && (
        <div className="rounded-2xl bg-sky-50 border border-sky-200 p-3 flex items-center gap-3">
          <MapPin className="h-5 w-5 text-sky-600 flex-shrink-0"/>
          <div className="flex-1 text-sm">
            {coords ? (
              <div className="text-sky-700 font-bold">Location captured ✓</div>
            ) : (
              <div className="text-sky-700">Location required for this training</div>
            )}
            <div className="text-xs text-sky-700/70">Allowed: {info.locations.map(l=>l.name).join(", ") || "—"}</div>
          </div>
          <Button variant="outline" size="sm" onClick={captureLocation} disabled={locating} data-testid="quiz-capture-location">
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : (coords ? "Refresh" : "Allow")}
          </Button>
        </div>
      )}

      <Button onClick={start} disabled={submitting}
        data-testid="quiz-start-btn"
        className="w-full h-14 rounded-2xl text-white font-bold text-lg shadow-lg"
        style={{ background: brandColor, boxShadow: `0 15px 35px -10px ${brandColor}` }}>
        {submitting ? <Loader2 className="h-5 w-5 animate-spin"/> : <><Play className="h-5 w-5 mr-2"/>{flowEnabled ? "Continue" : "Start Quiz"}</>}
      </Button>
    </div>
  );
}

function AssessmentLadder({ flowState, onStart, onTraining, onResetClick, onRefresh, submitting, brandColor }) {
  const { pre, training, post } = flowState;

  const preState = pre.exhausted && !pre.completed ? "exhausted" : (pre.completed ? "done" : "active");
  const trainingState = !pre.completed ? "locked" : (training.status === "completed" ? "done" : "active");
  let postState = "locked";
  if (post.unlocked) postState = post.exhausted ? "exhausted" : "active";

  // Bug fix: Even when Training is marked "done", allow the employee to re-open the material
  // as long as they still have Post-Test attempts remaining (they may want to revise before Post).
  const trainingActionable = training.asset && (
    trainingState === "active" ||
    (trainingState === "done" && post.unlocked && !post.exhausted)
  );
  const trainingBtnLabel =
    training.status === "in_progress" ? "Resume Training" :
    (training.status === "completed" ? "Re-open Training" : "Open Training");

  return (
    <div className="space-y-4" data-testid="assessment-ladder">
      <StepRow
        n={1} label="Pre-Test" icon={ShieldCheck} brandColor={brandColor}
        state={preState} testid="ladder-pre"
        meta={`${pre.attempts_used}/${pre.attempts_max} attempts${pre.best_pct !== null ? ` · best ${pre.best_pct}%` : ""}`}
        action={
          preState === "active" ? (
            <Button onClick={() => onStart("pre")} disabled={submitting} className="rounded-xl" style={{ background: brandColor, color: "white" }} data-testid="ladder-pre-start">
              {pre.completed ? "Retake Pre-Test" : "Start Pre-Test"}
            </Button>
          ) : preState === "exhausted" ? (
            <Button variant="outline" onClick={() => onResetClick("pre")} disabled={pre.reset_pending} data-testid="ladder-pre-reset">
              <RotateCw className="h-3.5 w-3.5 mr-1.5"/>{pre.reset_pending ? "Reset Pending" : "Request Reset"}
            </Button>
          ) : null
        }
      />
      <StepRow
        n={2} label="Training" icon={BookOpen} brandColor={brandColor}
        state={trainingState} testid="ladder-training"
        meta={training.status === "completed"
          ? `Completed${training.completed_at ? ` · ${new Date(training.completed_at).toLocaleDateString()}` : ""}`
          : (training.status === "in_progress" ? `In progress · ${training.total_open_seconds}s logged` : `${training.min_duration_sec}s minimum engagement`)}
        action={
          trainingActionable ? (
            <Button onClick={onTraining} className="rounded-xl" style={{ background: brandColor, color: "white" }} data-testid="ladder-training-open">
              <BookOpen className="h-3.5 w-3.5 mr-1.5"/>{trainingBtnLabel}
            </Button>
          ) : trainingState === "active" && !training.asset ? (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700">No material set</Badge>
          ) : null
        }
      />
      <StepRow
        n={3} label="Post-Test" icon={ShieldCheck} brandColor={brandColor}
        state={postState} testid="ladder-post"
        meta={(() => {
          let m = `${post.attempts_used}/${post.attempts_max} attempts`;
          if (post.best_pct !== null) m += ` · best ${post.best_pct}%`;
          if (post.expires_at) {
            const d = new Date(post.expires_at);
            m += ` · ${post.is_late ? "Window expired " : "Open until "}${d.toLocaleDateString()}`;
          }
          return m;
        })()}
        action={
          postState === "active" ? (
            <div className="flex flex-col items-end gap-1">
              <Button onClick={() => onStart("post")} disabled={submitting} className="rounded-xl" style={{ background: brandColor, color: "white" }} data-testid="ladder-post-start">
                Start Post-Test
              </Button>
              {post.is_late && <span className="text-[10px] text-amber-600 font-bold">Will be flagged Late</span>}
            </div>
          ) : postState === "exhausted" ? (
            <Button variant="outline" onClick={() => onResetClick("post")} disabled={post.reset_pending} data-testid="ladder-post-reset">
              <RotateCw className="h-3.5 w-3.5 mr-1.5"/>{post.reset_pending ? "Reset Pending" : "Request Reset"}
            </Button>
          ) : null
        }
      />

      <div className="text-center">
        <Button variant="ghost" size="sm" onClick={onRefresh} className="text-slate-500" data-testid="ladder-refresh">
          <RotateCw className="h-3.5 w-3.5 mr-1.5"/>Refresh status
        </Button>
      </div>
    </div>
  );
}

function StepRow({ n, label, icon: Icon, brandColor, state, meta, action, testid }) {
  const isDone = state === "done";
  const isLocked = state === "locked";
  const isExhausted = state === "exhausted";
  const bg = isDone ? "bg-emerald-50 border-emerald-200" : isLocked ? "bg-slate-50 border-slate-200 opacity-60" : isExhausted ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200";
  return (
    <div className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${bg}`} data-testid={testid}>
      <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0 text-white" style={{ background: isDone ? "#10b981" : isLocked ? "#94a3b8" : isExhausted ? "#d97706" : brandColor }}>
        {isDone ? <CheckCircle2 className="h-5 w-5"/> : isLocked ? <Lock className="h-5 w-5"/> : isExhausted ? <Clock className="h-5 w-5"/> : <Icon className="h-5 w-5"/>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Step {n}</span>
          {isDone && <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[10px]">Done</Badge>}
          {isLocked && <Badge variant="secondary" className="bg-slate-200 text-slate-600 text-[10px]">Locked</Badge>}
          {isExhausted && <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-[10px]">Limit reached</Badge>}
        </div>
        <div className="font-bold text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-500 truncate">{meta}</div>
      </div>
      <div className="flex-shrink-0">{action}</div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen bg-sky-50 grid place-items-center">{children}</div>;
}
function Pill({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold bg-slate-100 text-slate-700">
      <Icon className="h-3.5 w-3.5 text-sky-600"/>{text}
    </span>
  );
}
